/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-26
 */

/**
 * Ventana de la DECISION de un aprobador sobre uno o varios documentos: notas (HT_FAT_006) y
 * facturas (HT_FAT_004). Se abre desde la BANDEJA -por fila o en lote-, nunca desde la consulta,
 * que es solo lectura.
 *
 * Es un modal del manual 3.0 (560px): el titulo dice lo que se va a hacer y el documento se LEE en
 * una ficha. Al APROBAR solo se confirma; al RECHAZAR se escribe la observacion y se confirma con
 * boton de peligro. No cierra con clic fuera, para no perder lo escrito.
 *
 * No conoce el recurso: quien la abre le da cada documento con sus datos ya rotulados y los
 * textos ya traducidos, de modo que una sola ventana sirve a toda aprobacion.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import {
  ButtonComponent,
  DialogBodyDirective,
  DialogFooterComponent,
  DialogHeaderDirective,
  DialogRef,
  FieldLabelComponent,
  FormatService,
  IconComponent,
  TextareaComponent,
} from 'nebula-ui-kit';
import { FORMATO_MONEDA, porPerfil, VarianteBoton } from '@nebula-shared/data-access';

/** Un dato de la ficha: su rotulo y su valor, ya resueltos. */
export interface DatoDeLaFicha {
  rotulo: string;
  valor: string;
  /** Ocupa el ancho entero de la ficha: nombres largos. */
  ancho?: boolean;
}

/** Lo que se decide: aprobar solo confirma; rechazar pide la observacion. */
export type ModoDeDecision = 'aprobar' | 'rechazar';

/** Un documento tal como la ficha lo muestra, con sus rotulos ya resueltos. */
export interface FichaDeDocumento {
  id: number;
  tipo: string;
  /** Numero, o el texto que dice que aun no lo tiene. */
  numero: string;
  /** Nombre corto para la linea del lote: el tercero. */
  tercero: string;
  valor: number;
  /** Lo que se lee en la ficha de un documento solo. */
  datos: DatoDeLaFicha[];
}

/** Textos de la ventana, ya traducidos. Los de la observacion solo se usan al rechazar. */
export interface TextosDeLaDecision {
  total: string;
  confirmar: string;
  cancelar: string;
  observacion?: string;
  ejemplo?: string;
  rango?: string;
  /** Error con la observacion vacia; sin el, se usa el de debajo del minimo o `rango`. */
  requerido?: string;
  /** Error por debajo del minimo; sin el, `rango`. */
  minimo?: string;
  /** Error por encima del maximo; sin el, `rango`. */
  maximo?: string;
  /** Error cuando la observacion trae caracteres que el patron no admite. Sin el, se usa `rango`. */
  caracteres?: string;
}

/** Lo que la ventana recibe. */
export interface DatosDeLaDecision {
  modo: ModoDeDecision;
  titulo: string;
  aviso: string;
  documentos: FichaDeDocumento[];
  /** Rango de la observacion: solo al rechazar. */
  minimo?: number;
  maximo?: number;
  /**
   * Caracteres que admite la observacion, si la pantalla los restringe (p. ej. los motivos de
   * rechazo y de liberacion de presupuesto: alfanumericos, espacios, puntos, comas y guiones). Sin
   * patron no se restringe nada, como hasta ahora. Si trae la bandera `g` o `y` no importa: la
   * ventana reinicia su `lastIndex` antes de cada evaluacion.
   */
  patron?: RegExp;
  textos: TextosDeLaDecision;
}

/**
 * Lo que devuelve la ventana al confirmar: la observacion al rechazar, `true` al aprobar. Al
 * cancelar devuelve `undefined`.
 */
export type ResultadoDeLaDecision = string | true;

@Component({
  selector: 'app-decision-dialog',
  standalone: true,
  imports: [
    ButtonComponent,
    DialogBodyDirective,
    DialogFooterComponent,
    DialogHeaderDirective,
    FieldLabelComponent,
    IconComponent,
    TextareaComponent,
  ],
  templateUrl: './decision-dialog.component.html',
  styleUrls: ['./decision-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionDialogComponent {
  private readonly dialogRef =
    inject<DialogRef<DecisionDialogComponent, ResultadoDeLaDecision>>(DialogRef);

  /** La moneda como la pinta el kit: una sola forma en el ERP (decision de Carlos, 19-oct). */
  private readonly formato = inject(FormatService);

  /** Lo que se decide y como se dice. */
  readonly datos = input.required<DatosDeLaDecision>();

  /** Cancelar sale sin consecuencias: en el perfil 3.0 es un boton de solo texto. */
  protected readonly varianteCancelar = porPerfil<VarianteBoton>('text', 'secondary');

  /** Se rechaza: hay observacion que escribir. */
  protected readonly rechaza = computed(() => this.datos().modo === 'rechazar');

  /** Observacion escrita. */
  protected readonly observacion = signal('');

  /** Ya se intento rechazar: desde aqui el error se muestra. */
  protected readonly tocado = signal(false);

  /** Un solo documento: se lee su ficha completa. Varios: la lista con su total. */
  protected readonly unDocumento = computed(() => this.datos().documentos.length === 1);

  /**
   * Un importe como lo pinta el kit.
   *
   * @param valor el importe.
   * @returns su texto.
   */
  protected moneda(valor: number | null | undefined): string {
    return this.formato.formatValue(valor, FORMATO_MONEDA);
  }

  /** Suma de lo que se rechaza en lote. */
  protected readonly total = computed(() =>
    this.datos().documentos.reduce((suma, doc) => suma + (doc.valor ?? 0), 0),
  );

  /** Largo util de la observacion. */
  private readonly largo = computed(() => this.observacion().trim().length);

  /** La observacion cumple su rango (RN-ALC1-05 de notas, RN-ALC1-11 de facturas). */
  private readonly enRango = computed(
    () =>
      this.largo() >= (this.datos().minimo ?? 0) &&
      this.largo() <= (this.datos().maximo ?? Number.MAX_SAFE_INTEGER),
  );

  /** La observacion solo trae los caracteres que admite el patron, si la pantalla lo declara. */
  private readonly caracteresValidos = computed(() => {
    const patron = this.datos().patron;
    if (!patron) return true;
    // Con `g` o `y` la expresion recuerda donde quedo: sin reiniciarla, el mismo texto alternaria
    // entre valido e invalido en cada evaluacion.
    patron.lastIndex = 0;
    return patron.test(this.observacion().trim());
  });

  /** La observacion se puede enviar: rango y, si hay patron, caracteres. */
  protected readonly valida = computed(() => this.enRango() && this.caracteresValidos());

  /** El error que se muestra tras intentar rechazar: el del rango primero, luego el de caracteres. */
  protected readonly error = computed(() => {
    if (!this.tocado() || this.valida()) return '';
    const textos = this.datos().textos;
    return this.enRango() ? (textos.caracteres ?? textos.rango ?? '') : this.errorDelRango(textos);
  });

  /**
   * Fuera de rango: vacio, corto o largo con su propio texto si la pantalla lo da; si no, `rango`,
   * como siempre.
   *
   * @param textos los textos de la ventana.
   * @returns el error que corresponde.
   */
  private errorDelRango(textos: TextosDeLaDecision): string {
    const largo = this.largo();
    if (largo === 0 && textos.requerido) return textos.requerido;
    if (largo < (this.datos().minimo ?? 0)) return textos.minimo ?? textos.rango ?? '';
    return textos.maximo ?? textos.rango ?? '';
  }

  /** Confirma: al aprobar, sin mas; al rechazar, con la observacion si cumple su rango. */
  protected confirmar(): void {
    if (!this.rechaza()) {
      this.dialogRef.close(true);
      return;
    }
    this.tocado.set(true);
    if (!this.valida()) return;
    this.dialogRef.close(this.observacion().trim());
  }

  /** Cierra sin decidir. */
  protected cancelar(): void {
    this.dialogRef.close(undefined);
  }
}
